import React from 'react';
import PropTypes from 'prop-types';
import {
  QuantityWrapper,
  StepperButton,
  QuantityValue,
  StepperPlusIcon,
  StepperMinusIcon,
} from './Common';

const MAX_CHARS = 10;
const MAX_VALUE = 9999999999;

// Small controlled quantity stepper. Mirrors the increment/decrement clamping
// from BuyInputFields (that stepper is too Buy/Formik-coupled to reuse). Reports
// the next value through onChange — an empty string once it drops to 0, so the
// caller can treat 0 as "no quantity". The "−" button self-disables at 0.
const QuantityStepper = ({ value, onChange, max }) => {
  const current = Number(value) || 0;
  const ceiling = max ?? MAX_VALUE;
  const isMinReached = current <= 0;
  const isMaxReached = current >= ceiling;

  const handleDecrease = () => {
    if (isMinReached) return;
    const next = current - 1;
    onChange(next > 0 ? next : '');
  };

  const handleIncrease = () => {
    if (isMaxReached) return;
    const next = current + 1;
    if (String(next).length > MAX_CHARS) return;
    onChange(next);
  };

  // Free-typed entry: keep digits only, clamp length and (when set) the max, and
  // report an empty string for a cleared/zero field so gating treats it as "no
  // quantity" — consistent with the decrement path.
  const handleType = (event) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, MAX_CHARS);
    if (digits === '') {
      onChange('');
      return;
    }
    const clamped = Math.min(Number(digits), ceiling);
    onChange(clamped > 0 ? clamped : '');
  };

  return (
    <QuantityWrapper>
      <StepperButton
        type="button"
        onClick={handleDecrease}
        disabled={isMinReached}
        aria-label="decrease quantity"
        data-cy="gtt-quantity-decrease"
      >
        <StepperMinusIcon />
      </StepperButton>
      <QuantityValue
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="0"
        value={value === '' || value == null ? '' : current}
        onChange={handleType}
        aria-label="quantity"
        data-cy="gtt-quantity-value"
      />
      <StepperButton
        type="button"
        onClick={handleIncrease}
        disabled={isMaxReached}
        aria-label="increase quantity"
        data-cy="gtt-quantity-increase"
      >
        <StepperPlusIcon />
      </StepperButton>
    </QuantityWrapper>
  );
};

QuantityStepper.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  max: PropTypes.number,
};

export default QuantityStepper;
